import { ref, unref } from 'vue';
import SalesTransactionDocumentsAPI from 'dashboard/api/salesTransactionDocuments';
import {
  uploadSalesDocumentFiles,
  validateSalesDocumentFile,
} from './salesDocumentFiles';
export function useSalesPropertyDocuments({ id, t, notify }) {
  const documents = ref([]);
  const documentTypes = ref([]);
  const pending = ref([]);
  const loading = ref(false);
  const loaded = ref(false);
  const loadDocuments = async () => {
    const response = await SalesTransactionDocumentsAPI.list(unref(id));
    documents.value = response?.data?.data || [];
    return documents.value;
  };

  const loadDocumentTypes = async () => {
    const response = await SalesTransactionDocumentsAPI.catalog(unref(id));
    documentTypes.value = response?.data?.data || [];
    return documentTypes.value;
  };
  const load = async ({ includeCatalog = true } = {}) => {
    if (!unref(id)) return [];
    loading.value = true;
    try {
      const requests = [loadDocuments()];
      if (includeCatalog) requests.push(loadDocumentTypes());
      await Promise.all(requests);
      return documents.value;
    } catch (error) {
      notify(
        error?.response?.data?.message ||
          t('SALES_PROPERTY.DOCUMENTS.LOAD_ERROR')
      );
      return [];
    } finally {
      loading.value = false;
      loaded.value = true;
    }
  };

  const uploadFile = async file => {
    const item = {
      key: `${file.name}-${file.size}-${Date.now()}`,
      name: file.name,
      progress: 0.1,
      error: '',
    };
    pending.value = [...pending.value, item];
    const validationError = validateSalesDocumentFile(file, {
      extensions: ['pdf'],
      formatError: t('SALES_PROPERTY.DOCUMENTS.FORMAT_ERROR'),
      mimeTypes: ['application/pdf'],
      sizeError: t('SALES_PROPERTY.DOCUMENTS.SIZE_ERROR'),
    });
    if (validationError) {
      item.error = validationError;
      pending.value = [...pending.value];
      return false;
    }

    try {
      const response = await SalesTransactionDocumentsAPI.upload(
        unref(id),
        file,
        event => {
          item.progress = Math.round(
            (event.loaded / (event.total || file.size)) * 100
          );
          pending.value = [...pending.value];
        }
      );
      documents.value = [
        ...documents.value.filter(
          document => document.id !== response.data.data.id
        ),
        response.data.data,
      ];
      pending.value = pending.value.filter(entry => entry.key !== item.key);
      return true;
    } catch (error) {
      item.error =
        error?.response?.data?.message ||
        t('SALES_PROPERTY.DOCUMENTS.UPLOAD_ERROR');
      pending.value = [...pending.value];
      return false;
    }
  };

  const uploadFiles = async files => {
    const results = await uploadSalesDocumentFiles(files, uploadFile);
    const changed = results.some(Boolean);
    if (changed) notify(t('SALES_PROPERTY.DOCUMENTS.UPLOAD_SUCCESS'));
    return changed;
  };

  const dismissPending = item => {
    pending.value = pending.value.filter(entry => entry.key !== item?.key);
  };

  const remove = async document => {
    try {
      await SalesTransactionDocumentsAPI.remove(unref(id), document.id);
      documents.value = documents.value.filter(item => item.id !== document.id);
      notify(t('SALES_PROPERTY.DOCUMENTS.REMOVE_SUCCESS'));
      return true;
    } catch (error) {
      notify(
        error?.response?.data?.message ||
          t('SALES_PROPERTY.DOCUMENTS.REMOVE_ERROR')
      );
      return false;
    }
  };

  const updateTypes = async (document, types) => {
    try {
      const response = await SalesTransactionDocumentsAPI.update(
        unref(id),
        document.id,
        types
      );
      documents.value = documents.value.map(item =>
        item.id === document.id ? response?.data?.data || item : item
      );
      notify(t('SALES_PROPERTY.DOCUMENTS.UPDATE_SUCCESS'));
      return true;
    } catch (error) {
      notify(
        error?.response?.data?.message ||
          t('SALES_PROPERTY.DOCUMENTS.UPDATE_ERROR')
      );
      return false;
    }
  };

  const download = async document => {
    try {
      const response = await SalesTransactionDocumentsAPI.signedUrl(
        unref(id),
        document.id
      );
      window.open(response.data.data.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      notify(
        error?.response?.data?.message ||
          t('SALES_PROPERTY.DOCUMENTS.DOWNLOAD_ERROR')
      );
    }
  };

  return {
    documents,
    documentTypes,
    dismissPending,
    download,
    load,
    loaded,
    loading,
    pending,
    remove,
    updateTypes,
    uploadFiles,
  };
}
